import {inject, Injectable, Injector} from '@angular/core';
import {Observable, of, ReplaySubject, throwError} from 'rxjs';
import {take} from 'rxjs/operators';
import {MessengerCardsPort} from './cards.port';
import {AttachmentLink} from './messenger.models';

/**
 * A FILE TO OPEN (ITP-E-240-10), as the thread's file card and Details' Shared both name it: its own address (a file sent
 * from the Drive, or before chat files), or a chat file's id (B-19), whose link is minted when it is opened.
 */
export interface OpenableFile {
  name: string;
  url: string | null;
  attachmentId: string | null;
}

/** A file of the thread, or a tile or row of Details, as the open action takes it: the same file is the same value. */
export function openableFile(file: {name?: string, url?: string | null, attachmentId?: string | null}, name = file.name): OpenableFile {
  return {name: name || '', url: file.url || null, attachmentId: file.attachmentId || null};
}

/** A click the browser serves from the link's own address (a new window, a background tab): the action leaves it alone. */
export function opensByItsLink(event: MouseEvent): boolean {
  return event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey;
}

/**
 * THE ONE WAY A FILE OF THE CHAT IS OPENED (ITP-E-240-10). A click on a file in the thread's card and on its tile or row in
 * Details' Shared both come here, so a file opens, or downloads, the same way from either.
 *
 * Called inside the click. A file with an address of its own opens it in a new tab, `noopener,noreferrer` as Details' links
 * always did, and the server's answer decides whether the browser shows it or downloads it; an address that is not a web
 * address is never opened. A chat file opens a blank tab at once, so no pop-up blocker stops it, asks the server for a link
 * minted for this open (`MessengerCardsPort.downloadLink`) and sends the tab to it: a picture or a PDF to its preview link,
 * which the browser shows, anything else to its download. The answer is the open's outcome: one value once the file is on
 * its way, or an error when it cannot be opened, which each caller says in its own place.
 */
@Injectable({providedIn: 'root'})
export class MessengerFileOpener {
  // The port is read when a chat file is opened, so a caller of today's files needs nothing more.
  private readonly injector = inject(Injector);

  open(file: OpenableFile): Observable<void> {
    if (file.url) {
      if (!isWebAddress(file.url)) {
        return throwError(() => new Error(`Messenger: ${file.name} has no address a browser opens.`));
      }
      window.open(file.url, '_blank', 'noopener,noreferrer');
      return of(undefined);
    }
    if (!file.attachmentId) {
      return throwError(() => new Error(`Messenger: ${file.name} has nothing to open it by.`));
    }
    const tab = window.open('about:blank', '_blank');
    if (tab) {
      tab.opener = null;
    }
    const opened = new ReplaySubject<void>(1);
    let sent = false;
    this.link(file.attachmentId).subscribe({
      next: link => {
        sent = true;
        const target = link.previewUrl || link.url;
        if (tab) {
          tab.location.href = target;
        } else {
          window.open(target, '_blank', 'noopener,noreferrer');
        }
        opened.next();
        opened.complete();
      },
      error: error => {
        tab?.close();
        opened.error(error);
      },
      complete: () => {
        if (!sent) {
          tab?.close();
          opened.error(new Error(`Messenger B-19: no link came for ${file.name}.`));
        }
      }
    });
    return opened.asObservable();
  }

  /** B-19: a chat file's link, minted now: what a chat video plays from, and what a chat picture's preview is drawn from. */
  link(attachmentId: string): Observable<AttachmentLink> {
    return this.injector.get(MessengerCardsPort).downloadLink(attachmentId).pipe(take(1));
  }
}

function isWebAddress(url: string): boolean {
  try {
    return ['http:', 'https:'].includes(new URL(url, document.baseURI).protocol);
  } catch {
    return false;
  }
}
